import { useState, useEffect, useRef, useCallback } from 'react';
import { usePageTitle, godotAppUrl } from '../appInfo';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/api';
import ResetReplayButton from '../components/ResetReplayButton';
import AddToScheduleButton from '../components/AddToScheduleButton';
import {
  DUEL_STATUSES, STATUS_LABEL, DUEL_STAGES, STAGE_LABEL, STAGE_BADGE, SETTABLE_STAGES, HIDDEN_STAGES, GAME_CHECKS,
} from '../utils/duelStatuses';
import { DUEL_SORTS, DUEL_SORT_LABEL, sortDuels, loadDuelSort, saveDuelSort, formatUpdatedAt } from '../utils/duelSort';
import { formatServerDate, formatServerTime } from '../utils/serverTime';

function DeckSelect({ decks, players, value, onChange }) {
  const deckPlayers = [...new Set(decks.map(d => d.player || ''))].sort();
  const playerKanji = Object.fromEntries(players.map(p => [p.name, p.kanji || p.name]));
  const [deckPlayer, setDeckPlayer] = useState(deckPlayers[0] ?? '');
  const filtered = decks.filter(d => (d.player || '') === deckPlayer)
    .slice().sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'ja'));

  const handlePlayerChange = (e) => {
    const next = e.target.value;
    setDeckPlayer(next);
    const first = decks.find(d => (d.player || '') === next);
    if (first) onChange(String(first.id));
  };

  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <select className="form-select" value={deckPlayer} onChange={handlePlayerChange}>
        {deckPlayers.map(p => <option key={p} value={p}>{playerKanji[p] || p || 'その他'}</option>)}
      </select>
      <select className="form-select" value={value} onChange={e => onChange(e.target.value)}>
        {filtered.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
      </select>
    </div>
  );
}

function AddDuelModal({ players, decks, onClose, onAdded }) {
  const [name, setName] = useState('');
  const [p1, setP1] = useState(players[0]?.name ?? '');
  const [p2, setP2] = useState(players[1]?.name ?? players[0]?.name ?? '');
  const [d1, setD1] = useState(decks[0]?.id ?? '');
  const [d2, setD2] = useState(decks[0]?.id ?? '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.project('AddDuel', { name, player1_id: p1, deck1_id: Number(d1), player2_id: p2, deck2_id: Number(d2) });
      onAdded();
    } catch (err) {
      alert('作成に失敗しました: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">デュエルを追加</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">デュエル名</label>
            <input className="form-input" value={name} onChange={e => setName(e.target.value)} required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">プレイヤー1</label>
              <select className="form-select" value={p1} onChange={e => setP1(e.target.value)}>
                {players.map(p => <option key={p.name} value={p.name}>{p.kanji || p.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">デッキ1</label>
              <DeckSelect decks={decks} players={players} value={d1} onChange={setD1} />
            </div>
            <div className="form-group">
              <label className="form-label">プレイヤー2</label>
              <select className="form-select" value={p2} onChange={e => setP2(e.target.value)}>
                {players.map(p => <option key={p.name} value={p.name}>{p.kanji || p.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">デッキ2</label>
              <DeckSelect decks={decks} players={players} value={d2} onChange={setD2} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>キャンセル</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '作成中...' : '作成'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PlayerCard({ info, deckInfo, reverse }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexDirection: reverse ? 'row-reverse' : 'row', gap: 8, flex: 1, minWidth: 0 }}>
      <PlayerIcon info={info} size={36} />
      <div style={{ minWidth: 0, textAlign: reverse ? 'right' : 'left' }}>
        <div style={{ fontWeight: 'bold', fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {info?.kanji || info?.name || '—'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: reverse ? 'flex-end' : 'flex-start', gap: 4, marginTop: 2 }}>
          <DeckIcon info={deckInfo} />
          <span style={{ fontSize: '0.8rem', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {deckInfo?.name ?? 'N/A'}
          </span>
        </div>
      </div>
    </div>
  );
}

function PlayerIcon({ info, size = 32 }) {
  if (!info?.image_thumbnail_url) return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: '#333', flexShrink: 0 }} />
  );
  return (
    <img
      src={info.image_thumbnail_url}
      alt={info.kanji || info.name}
      title={info.kanji || info.name}
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
    />
  );
}

function DeckIcon({ info }) {
  if (!info?.deck_illust_thumbnail_url) return null;
  return (
    <img
      src={info.deck_illust_thumbnail_url}
      alt={info.name}
      title={info.name}
      style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
    />
  );
}

function StatusSelect({ gameUuid, current, onChange }) {
  const [saving, setSaving] = useState(false);

  const handleChange = async (e) => {
    const next = e.target.value;
    setSaving(true);
    try {
      await api.duel('SetDuelStatus', { game_uuid: gameUuid, status: next });
      onChange(next);
    } catch (err) {
      alert('ステータス更新に失敗しました: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <select
      className="form-select"
      value={current}
      onChange={handleChange}
      disabled={saving}
      style={{ fontSize: '0.8rem', padding: '2px 6px', height: 'auto' }}
      onClick={e => e.stopPropagation()}
    >
      {DUEL_STATUSES.map(s => (
        <option key={s} value={s}>{STATUS_LABEL[s]}</option>
      ))}
    </select>
  );
}

/** デュエルの段（Issue #988）。人が選ぶ（試合のチェックリストとは連動しない）。archived はアーカイブのジョブが付けるので表示だけ */
function StageSelect({ duelId, current, onChange }) {
  const [saving, setSaving] = useState(false);
  if (current === 'archived') {
    return <span className={`badge ${STAGE_BADGE.archived}`} style={{ fontSize: '0.72rem' }} data-testid="duel-stage">{STAGE_LABEL.archived}</span>;
  }
  const handleChange = async (e) => {
    const next = e.target.value;
    setSaving(true);
    try {
      await api.project('SetDuelStage', { duel_id: duelId, stage: next });
      onChange(next);
    } catch (err) {
      alert('段の更新に失敗しました: ' + err.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <select
      className="form-select" value={current ?? 'open'} onChange={handleChange} disabled={saving} data-testid="duel-stage"
      style={{ fontSize: '0.8rem', padding: '2px 6px', height: 'auto' }} onClick={e => e.stopPropagation()}
    >
      {SETTABLE_STAGES.map(s => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
    </select>
  );
}

/** 試合のチェックリスト（決着・位置合わせ・書き出し。本番が数える。Issue #988） */
function GameChecks({ checks }) {
  return (
    <span style={{ display: 'inline-flex', gap: 8 }} data-testid="game-checks">
      {GAME_CHECKS.map(([key, label]) => (
        <span key={key} style={{ fontSize: '0.75rem', color: checks?.[key] ? '#7fd38f' : '#666' }} title={label}>
          {checks?.[key] ? '☑' : '☐'} {label}
        </span>
      ))}
    </span>
  );
}

const EXPORT_STATUS_LABEL = {
  pending:    '待機中',
  processing: '処理中',
  completed:  '完了',
  failed:     '失敗',
  cancelled:  'キャンセル済',
};

const EXPORT_STATUS_BADGE = {
  pending:    { background: '#5a4e00', color: '#f5d65e' },
  processing: { background: '#1e6b3a', color: '#6ee89a' },
  completed:  { background: '#444',    color: '#aaa' },
  failed:     { background: '#6b1e1e', color: '#e89a9a' },
  cancelled:  { background: '#3a3a3a', color: '#888' },
};

function GameNameEditor({ gameId, initialName, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialName ?? '');
  const [saving, setSaving] = useState(false);

  const startEdit = (e) => {
    e.stopPropagation();
    setValue(initialName ?? '');
    setEditing(true);
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    setSaving(true);
    try {
      await api.setGameName(gameId, value);
      onSaved(value);
      setEditing(false);
    } catch (err) {
      alert('名前の保存に失敗しました: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setEditing(false);
  };

  if (!editing) {
    return (
      <span
        onClick={startEdit}
        style={{ fontSize: '0.82rem', color: initialName ? '#c0d8f0' : '#555', cursor: 'pointer', minWidth: 60 }}
        title="クリックして名前を編集"
      >
        {initialName || '名前なし'}
      </span>
    );
  }

  return (
    <span style={{ display: 'flex', gap: 4, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
      <input
        autoFocus
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handleSave(e); if (e.key === 'Escape') handleCancel(e); }}
        style={{ fontSize: '0.82rem', padding: '1px 6px', background: '#0f3460', border: '1px solid #1a4080', borderRadius: 4, color: '#e0e0e0', width: 120 }}
      />
      <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving} style={{ padding: '1px 6px' }}>保存</button>
      <button className="btn btn-sm btn-secondary" onClick={handleCancel} style={{ padding: '1px 6px' }}>✕</button>
    </span>
  );
}

function ExportRequestList({ gameId }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [cancelling, setCancelling] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(() => {
    api.getExportRequests({ game_id: gameId })
      .then(data => {
        setRequests(data);
        setLoading(false);
        const hasActive = data.some(r => r.status === 'pending' || r.status === 'processing');
        if (hasActive) {
          timerRef.current = setTimeout(load, 5000);
        }
      })
      .catch(() => setLoading(false));
  }, [gameId]);

  useEffect(() => {
    load();
    return () => clearTimeout(timerRef.current);
  }, [load]);

  // Godot のテーマ aviutl で AviUtl の出力と同じ成果物（Issue #969）。AviUtl そのものの出力（kind aviutl_output）は Issue #998 で消した
  const handleCreateGodot = async (cut = false) => {
    try {
      // cut: 発話と演出の区間だけに切り取る（Issue #976）
      await (cut ? api.createGodotCutOutputRequest(gameId) : api.createGodotAviutlOutputRequest(gameId));
      clearTimeout(timerRef.current);
      load();
    } catch (err) {
      alert('リクエスト作成に失敗しました: ' + err.message);
    }
  };

  const handleCancel = async (reqId) => {
    setCancelling(reqId);
    try {
      await api.cancelExportRequest(reqId);
      clearTimeout(timerRef.current);
      load();
    } catch (err) {
      alert('キャンセルに失敗しました: ' + err.message);
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid #0f3460', paddingTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: '0.85rem', color: '#aaa' }}>エクスポートリクエスト</span>
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="btn btn-sm btn-secondary" onClick={() => handleCreateGodot()}
            title="Godot のテーマ aviutl で AviUtl の出力と同じもの（output.mp4・splits・演出区間）をワーカーの PC の出力先（俯瞰視点_godot）に作る">
            ＋ Godot で AviUtl 出力
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => handleCreateGodot(true)}
            title="Godot で書き出して発話と演出の区間だけに切り取る。映像だけの video.mp4 と、話者ごとのピンマイク・録画の音・SE の wav をワーカーの PC の出力先（発話と演出_godot）に作る">
            ＋ Godot で発話と演出だけ
          </button>
        </span>
      </div>

      {loading && <p style={{ fontSize: '0.8rem', color: '#666' }}>読み込み中...</p>}

      {!loading && requests.length === 0 && (
        <p style={{ fontSize: '0.8rem', color: '#666' }}>リクエストなし</p>
      )}

      {requests.map(req => (
        <div key={req.id} style={{
          background: '#0f1e3a', border: '1px solid #1a3060', borderRadius: 6,
          padding: '8px 12px', marginBottom: 6,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.75rem', color: '#666' }}>#{req.id}</span>
            <span style={{
              ...EXPORT_STATUS_BADGE[req.status],
              display: 'inline-block', padding: '1px 7px',
              borderRadius: 4, fontSize: '0.75rem',
            }}>
              {EXPORT_STATUS_LABEL[req.status] ?? req.status}
            </span>
            {req.worker_id && (
              <span style={{ fontSize: '0.75rem', color: '#666' }}>{req.worker_id}</span>
            )}
            {(req.kind == null || req.kind === 'aviutl_output') && (
              <span style={{ fontSize: '0.75rem', color: '#888' }}>AviUtl（旧）</span>
            )}
            {req.kind === 'godot_aviutl_output' && (
              <span style={{ fontSize: '0.75rem', color: '#6699cc' }}>Godot</span>
            )}
            {req.kind === 'godot_cut_output' && (
              <span style={{ fontSize: '0.75rem', color: '#6699cc' }}>Godot・発話と演出だけ</span>
            )}
            {Array.isArray(req.runs) && (
              <span style={{ fontSize: '0.75rem', color: '#6699cc' }}>
                runs×{req.runs.length}
              </span>
            )}
            <span style={{ fontSize: '0.75rem', color: '#555', marginLeft: 'auto' }}>
              {formatServerTime(req.created_at)}
            </span>
            {(req.status === 'pending' || req.status === 'processing') && (
              <button
                className="btn btn-sm btn-danger"
                onClick={() => handleCancel(req.id)}
                disabled={cancelling === req.id}
              >
                {cancelling === req.id ? '...' : 'キャンセル'}
              </button>
            )}
          </div>
          {req.progress && (
            <div style={{ fontSize: '0.78rem', color: '#8ab4f8', marginTop: 4 }}>
              {req.progress}
            </div>
          )}
          {req.error_message && (
            <div style={{ fontSize: '0.78rem', color: '#e89a9a', marginTop: 4 }}>
              {req.error_message}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ArchiveDuelDialog({ duel, onClose, onArchived }) {
  const [step, setStep] = useState(1); // 1=説明, 2=最終確認
  const [archiving, setArchiving] = useState(false);

  const handleArchive = async () => {
    setArchiving(true);
    try {
      const result = await api.project('CreateArchiveDuelRequest', { duel_id: duel.id });
      if (result?.error) throw new Error(result.error);
      onArchived();
    } catch (err) {
      alert('アーカイブの開始に失敗しました: ' + err.message);
      setArchiving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        {step === 1 ? (
          <>
            <h2 className="modal-title" style={{ color: '#8ab4f8' }}>🧊 デュエルのアーカイブ</h2>
            <p style={{ marginBottom: 8 }}>
              <strong>「{duel.name}」</strong> のS3上の動画・音声・ソースファイルをすべて
              Glacier Deep Archive（低頻度アクセス向けストレージ）へ移行します。
            </p>
            <p style={{ color: '#e0c05e', marginBottom: 16 }}>
              移行後はファイルの復元に12時間以上かかります（復元機能は現バージョンでは未実装です）。
              デュエルの記録・盤面ログ自体は削除されません。
            </p>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>キャンセル</button>
              <button className="btn btn-primary" onClick={() => setStep(2)}>次へ</button>
            </div>
          </>
        ) : (
          <>
            <h2 className="modal-title" style={{ color: '#8ab4f8' }}>アーカイブを開始しますか？</h2>
            <p style={{ marginBottom: 16 }}>
              <strong>「{duel.name}」</strong> のメディアファイルをアーカイブします。ファイル数によっては
              完了まで時間がかかります。よろしいですか？
            </p>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose} disabled={archiving}>キャンセル</button>
              <button className="btn btn-primary" onClick={handleArchive} disabled={archiving}>
                {archiving ? '開始中...' : 'アーカイブする'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ArchiveStatusBadge({ duel, onChanged }) {
  const [requests, setRequests] = useState([]);
  const timerRef = useRef(null);

  const load = useCallback(() => {
    api.getExportRequests({ duel_id: duel.id, kind: 'archive_duel' })
      .then(data => {
        setRequests(data);
        const hasActive = data.some(r => r.status === 'pending' || r.status === 'processing');
        if (hasActive) {
          timerRef.current = setTimeout(load, 5000);
        } else if (!duel.archived_at && data.some(r => r.status === 'completed')) {
          onChanged();
        }
      })
      .catch(() => {});
  }, [duel.id, duel.archived_at, onChanged]);

  useEffect(() => {
    load();
    return () => clearTimeout(timerRef.current);
  }, [load]);

  if (duel.archived_at) {
    return (
      <span style={{ background: '#1a2a4a', color: '#8ab4f8', fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4 }}>
        🧊 アーカイブ済み（{formatServerDate(duel.archived_at)}）
      </span>
    );
  }
  const active = requests.find(r => r.status === 'pending' || r.status === 'processing');
  if (active) {
    return (
      <span style={{ ...EXPORT_STATUS_BADGE[active.status], fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4 }}>
        アーカイブ{EXPORT_STATUS_LABEL[active.status]}{active.progress ? `: ${active.progress}` : ''}
      </span>
    );
  }
  const failed = requests.length > 0 && requests[0].status === 'failed';
  if (failed) {
    return (
      <span style={{ ...EXPORT_STATUS_BADGE.failed, fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4 }}>
        アーカイブ失敗: {requests[0].error_message || ''}
      </span>
    );
  }
  return null;
}

function DeleteDuelDialog({ duel, onClose, onDeleted }) {
  const [step, setStep] = useState(1); // 1=警告, 2=最終確認
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.project('DeleteDuel', { duel_id: duel.id });
      onDeleted();
    } catch (err) {
      alert('削除に失敗しました: ' + err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        {step === 1 ? (
          <>
            <h2 className="modal-title" style={{ color: '#e89a9a' }}>⚠️ デュエルの削除</h2>
            <p style={{ marginBottom: 8 }}>
              <strong>「{duel.name}」</strong> を削除しようとしています。
            </p>
            <p style={{ color: '#e89a9a', marginBottom: 16 }}>
              この操作はS3上の動画・音声・ソースファイルをすべて削除します。元に戻すことはできません。
            </p>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>キャンセル</button>
              <button className="btn btn-danger" onClick={() => setStep(2)}>次へ</button>
            </div>
          </>
        ) : (
          <>
            <h2 className="modal-title" style={{ color: '#e89a9a' }}>本当に削除しますか？</h2>
            <p style={{ marginBottom: 16 }}>
              <strong>「{duel.name}」</strong> のすべてのデータ（DB・S3ファイル）が完全に削除されます。本当によろしいですか？
            </p>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose} disabled={deleting}>キャンセル</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? '削除中...' : '完全に削除する'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function DuelList() {
  usePageTitle('デュエル');
  const [duels, setDuels] = useState([]);
  const [players, setPlayers] = useState([]);
  const [decks, setDecks] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState('active');
  // 並び（名前順 / 更新順）。次に開いたときも同じ並びで見たいので localStorage に覚える（Issue #616）
  const [sort, setSort] = useState(loadDuelSort);
  const changeSort = (next) => { setSort(next); saveDuelSort(next); };
  const [expandedExports, setExpandedExports] = useState(new Set());
  const [deletingDuel, setDeletingDuel] = useState(null);
  const [archivingDuel, setArchivingDuel] = useState(null);
  const navigate = useNavigate();

  const toggleExport = (duelId) => {
    setExpandedExports(prev => {
      const next = new Set(prev);
      next.has(duelId) ? next.delete(duelId) : next.add(duelId);
      return next;
    });
  };

  const load = () => api.project('GetDuelListProject').then(setDuels);

  useEffect(() => {
    load();
    api.project('GetPlayersList').then(setPlayers);
    api.project('GetDeckList').then(setDecks);
  }, []);

  const handleAddGame = async (e, duelId) => {
    e.stopPropagation();
    try {
      await api.addDuelGame(duelId);
      load();
    } catch (err) {
      alert('試合追加に失敗しました: ' + err.message);
    }
  };

  // 絞り込みはデュエルの段（Issue #988）。「進行中」は書き出し済み・アーカイブ済みを隠す
  const filtered = sortDuels(filter === 'all'
    ? duels
    : filter === 'active'
      ? duels.filter(d => !HIDDEN_STAGES.includes(d.stage ?? 'open'))
      : duels.filter(d => (d.stage ?? 'open') === filter), sort);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">デュエル一覧</h1>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>＋ デュエルを追加</button>
      </div>

      {showAdd && players.length > 0 && (
        <AddDuelModal
          players={players}
          decks={decks}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); load(); }}
        />
      )}
      {deletingDuel && (
        <DeleteDuelDialog
          duel={deletingDuel}
          onClose={() => setDeletingDuel(null)}
          onDeleted={() => { setDeletingDuel(null); load(); }}
        />
      )}
      {archivingDuel && (
        <ArchiveDuelDialog
          duel={archivingDuel}
          onClose={() => setArchivingDuel(null)}
          onArchived={() => { setArchivingDuel(null); load(); }}
        />
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        <button
          className={`btn btn-sm ${filter === 'active' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('active')}
        >
          進行中
        </button>
        <button
          className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('all')}
        >
          すべて
        </button>
        {DUEL_STAGES.map(s => (
          <button
            key={s}
            className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter(s)}
          >
            {STAGE_LABEL[s]}
          </button>
        ))}
        {/* 並び。絞り込みと同じ行の右端（折り返せば次の行）。押した並びを覚える */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }} data-testid="duel-list-sort">
          <span style={{ fontSize: '0.75rem', color: '#777' }}>並び</span>
          {DUEL_SORTS.map(s => (
            <button
              key={s}
              className={`btn btn-sm ${sort === s ? 'btn-primary' : 'btn-secondary'}`}
              aria-pressed={sort === s}
              onClick={() => changeSort(s)}
            >
              {DUEL_SORT_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 && <p className="loading">デュエルがありません</p>}

      {filtered.map(duel => (
        <div key={duel.id} className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/duels/${duel.id}`)}>
          {/* ヘッダー行。**折り返すこと** —— デュエル名＋試合数＋印＋ボタン3つで
              iPhone 縦の幅（358px）を超える。`marginLeft: auto` は折り返した先でも
              効くので、ボタンは次の行の右端にまとまる */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
            <span className="card-title">{duel.name}</span>
            <span onClick={e => e.stopPropagation()}>
              <StageSelect duelId={duel.id} current={duel.stage}
                onChange={next => setDuels(prev => prev.map(d => d.id === duel.id ? { ...d, stage: next } : d))} />
            </span>
            <span style={{ fontSize: '0.75rem', color: '#555' }}>{(duel.games ?? []).length}試合</span>
            {/* 更新日時（デュエル行と試合行の updated_at の最大。無い＝移行前で活動の記録が無い） */}
            {formatUpdatedAt(duel) && (
              <span style={{ fontSize: '0.75rem', color: '#555' }} title="最後に手を入れた時刻" data-testid="duel-updated-at">
                更新 {formatUpdatedAt(duel)}
              </span>
            )}
            <span onClick={e => e.stopPropagation()}>
              <ArchiveStatusBadge duel={duel} onChanged={load} />
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button
                className="btn btn-sm btn-secondary"
                onClick={e => handleAddGame(e, duel.id)}
              >＋ 試合</button>
              <button
                className="btn btn-sm btn-secondary"
                disabled={!!duel.archived_at}
                onClick={e => { e.stopPropagation(); setArchivingDuel(duel); }}
              >{duel.archived_at ? 'アーカイブ済み' : 'アーカイブ'}</button>
              <button
                className="btn btn-sm btn-danger"
                onClick={e => { e.stopPropagation(); setDeletingDuel(duel); }}
              >削除</button>
            </div>
          </div>

          {/* プレイヤー */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <PlayerCard info={duel.player_1_info} deckInfo={duel.player1_deck_info} reverse />
            <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#888', flexShrink: 0 }}>VS</span>
            <PlayerCard info={duel.player_2_info} deckInfo={duel.player2_deck_info} />
          </div>

          {/* ゲーム一覧 */}
          {(duel.games ?? []).map(game => (
            <div key={game.id} onClick={e => e.stopPropagation()}
              style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, padding: '5px 8px',
                borderTop: '1px solid #1a1a1a', fontSize: '0.82rem' }}>
              <span style={{ color: '#555', minWidth: 36 }}>G{game.game_number}</span>
              {game.status === 'disabled'
                ? <span className="badge" style={{ fontSize: '0.7rem' }}>{STATUS_LABEL.disabled}</span>
                : <GameChecks checks={game.checks} />}
              <span
                className={`badge ${game.speech_review_cut_overview ? 'badge-active' : 'badge-pending'}`}
                style={{ fontSize: '0.7rem' }}
              >
                {game.speech_review_cut_overview ? 'レビュー済み' : '未レビュー'}
              </span>
              <GameNameEditor
                gameId={game.id}
                initialName={game.name}
                onSaved={newName => {
                  setDuels(prev => prev.map(d =>
                    d.id !== duel.id ? d : {
                      ...d,
                      games: d.games.map(g => g.id === game.id ? { ...g, name: newName } : g),
                    }
                  ));
                }}
              />
              {/* 外側の行が折り返しても、このボタン群自体も入り切らないことがある */}
              <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <button
                  className="btn btn-sm btn-primary"
                  title="再現エンジンの入力画面（Issue #467）"
                  data-testid="duel-list-open-input"
                  onClick={() => navigate(`/duels/${duel.id}/game/${game.game_uuid}/input`)}
                >入力</button>
                <button
                  className="btn btn-sm btn-primary"
                  title="実写に演出を重ねて時刻を合わせる（/duel-timing。Issue #643）"
                  data-testid="duel-list-open-edit"
                  onClick={() => navigate(`/duel-timing?duel=${duel.id}&game=${game.game_uuid}`)}
                >演出</button>
                <a
                  className="btn btn-sm btn-secondary"
                  title="Godot のブラウザ版で開く（席で遊ぶ・位置合わせ。この試合を選んだ状態で別のタブに）"
                  data-testid="duel-list-open-godot"
                  href={godotAppUrl(game.game_uuid)}
                  target="_blank"
                  rel="noreferrer"
                >Godot</a>
                <ResetReplayButton gameUuid={game.game_uuid} />
                <AddToScheduleButton gameUuid={game.game_uuid} />
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => toggleExport(game.id)}
                >エクスポート</button>
                <StatusSelect
                  gameUuid={game.game_uuid}
                  current={game.status}
                  onChange={next => {
                    setDuels(prev => prev.map(d =>
                      d.id !== duel.id ? d : {
                        ...d,
                        games: d.games.map(g => g.id === game.id ? { ...g, status: next } : g),
                      }
                    ));
                  }}
                />
              </div>
            </div>
          ))}

          {/* エクスポートパネル（ゲームごと） */}
          {(duel.games ?? []).filter(g => expandedExports.has(g.id)).map(game => (
            <div key={`exp-${game.id}`} onClick={e => e.stopPropagation()}
              style={{ borderTop: '1px solid #1a1a1a', padding: '0 8px' }}>
              <ExportRequestList gameId={game.id} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
